import { getReadableShiftCodeTextColor } from "@/utils/fontSizeUtils";
import type * as type from "@vTypes";
import type { JSX } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  sortShiftsByShiftGbThenSequence,
  type ShiftWithBand,
} from "@/hooks/shift/useSortShift";
import RosterListButton from "../../../components/feature/roster_view/RosterListButton";
import ViewRosterCalendar from "../../../components/feature/roster_view/ViewRosterCalendar";
import { toPng } from "html-to-image";
import {
  getDefaultShiftsSortedBySequence,
  normalizeScheduleCellCode,
} from "@utils/shift-util.ts";
import { getSnapshotCellDisplayColor } from "@/utils/snapshotRosterDisplay";
import type {
  IssuedSnapshotAssignmentEntry,
  Roster,
  RosterNurse,
} from "@vTypes/snapshot-type";
import useMyIssuedRoster, {
  type MyIssuedRosterTransfer,
} from "@/hooks/useMyIssuedRoster";
import useRosterConfig from "@/hooks/useRosterConfig";
import { myIssuedRosterResponseToRoster } from "@/utils/myIssuedRosterToRoster";
import { useMutation } from "@tanstack/react-query";
import {
  Check,
  Copy,
  Download,
  Eye,
  EyeOff,
  HelpCircle,
  Share2,
} from "lucide-react";
import { toast } from "@/components/toast/CustomToast";
import httpClient from "@/config/axios";
import CustomModal from "@/components/modal/CustomModal";
import LoadingSpiner from "@/components/ui/LoadingSpiner";
import { cn } from "@/utils/cn";
import IssuedDate from "./issued-date";
import { BtnIcon } from "@/components/ui/button/btn-icon";
import { Card, CardContent } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { UserData } from "@/types/user-type";
import {
  assignIndexPalette,
  mergeWardOptionsWithUserHome,
} from "@/pages/roster-view/utils/wardOptionsFromIssuedAssignments";

/** 내 근무표: `rosterSheet`에서 본인 행을 찾아 캘린더·요약에 표시 */

type ViewRosterCalendarProps = {
  currentScheduleDate: { year: number; month: number };
  holidayDates: string[];
  loginUser: type.LoginUser | null;
  rosterSheet: Roster;
  myAssignments?: IssuedSnapshotAssignmentEntry[];
  myTransfers?: MyIssuedRosterTransfer[];
  myDayGroupIds?: readonly (string | null)[];
  sourceWardGroupId?: string | null;
  selectedWardGroupId?: string | null;
  wardColorByGroupId?: Map<string, string>;
};
const ViewRosterCalendarC = ViewRosterCalendar as unknown as (
  props: ViewRosterCalendarProps
) => JSX.Element;

function withTransferDayGroupIds(
  dayGroupIds: readonly (string | null)[],
  transfers: readonly MyIssuedRosterTransfer[]
): readonly (string | null)[] {
  const next = [...dayGroupIds];
  for (const transfer of transfers) {
    const groupId = transfer.target_group_id.trim();
    if (groupId === "") continue;
    for (
      let day = transfer.period_start_day;
      day <= transfer.period_end_day;
      day += 1
    ) {
      if (day < 1) continue;
      next[day - 1] = groupId;
    }
  }
  return next;
}

function WardFilterChip({
  label,
  selected,
  dimmed,
}: {
  label: string;
  selected: boolean;
  dimmed: boolean;
}) {
  return (
    <span
      className={cn(
        "inline-flex h-9 max-w-[150px] items-center justify-center rounded-md border bg-white px-4 text-sm font-semibold font-pretendard transition-all",
        selected
          ? "border-sig text-sig"
          : "border-border text-muted-foreground hover:border-sig/35 hover:text-foreground",
        dimmed && "opacity-45"
      )}
    >
      <span className="min-w-0 truncate">{label}</span>
    </span>
  );
}

function WardFilterLabel() {
  return (
    <div className="inline-flex items-center gap-1.5 text-sm font-semibold font-pretendard text-secondary-foreground">
      <span>병동</span>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label="병동 필터 안내"
            className="inline-flex size-4 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-sig focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sig/30"
          >
            <HelpCircle className="size-4" aria-hidden="true" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" sideOffset={6}>
          <p>파견 또는 병동 배정 목록입니다. 선택한 병동의 근무만 강조합니다.</p>
        </TooltipContent>
      </Tooltip>
    </div>
  );
}

/** 스케줄에 등장한 근무코드 문자열을 스냅샷 카탈로그의 띠·시퀀스·id 순으로 정렬 */
function orderCodesBySnapshotCatalog(
  codes: string[],
  catalog: type.availableShifts[]
): string[] {
  if (codes.length === 0) return [];
  if (!catalog?.length) {
    return [...codes].sort((a, b) => a.localeCompare(b));
  }
  const used = catalog.filter(
    (s) =>
      codes.includes(s.shift_id) ||
      (!!s.default_shift && codes.includes(s.default_shift))
  );
  const sorted = sortShiftsByShiftGbThenSequence(used as ShiftWithBand[]);
  const ordered: string[] = [];
  const seen = new Set<string>();
  for (const s of sorted) {
    const key = codes.includes(s.shift_id)
      ? s.shift_id
      : s.default_shift && codes.includes(s.default_shift)
        ? s.default_shift
        : null;
    if (key != null && !seen.has(key)) {
      seen.add(key);
      ordered.push(key);
    }
  }
  for (const c of [...codes].sort((a, b) => a.localeCompare(b))) {
    if (!seen.has(c)) ordered.push(c);
  }
  return ordered;
}

function countForShiftRow(
  shift: type.availableShifts,
  codeCounts: Record<string, number>
): number {
  let n = 0;
  for (const [k, v] of Object.entries(codeCounts)) {
    if (k === shift.shift_id || k === (shift.default_shift ?? "")) n += v;
  }
  return n;
}

function chipColorForCatalogShift(
  shift: type.availableShifts,
  me: RosterNurse | undefined,
  shiftColorsMap: Record<string, string>
): string {
  const firstCell = me?.schedule?.find((cell) => {
    const c = normalizeScheduleCellCode(cell);
    return c === shift.shift_id || c === (shift.default_shift ?? "");
  });
  const fromSnap = getSnapshotCellDisplayColor(
    firstCell ?? { code: shift.shift_id, color: shift.color ?? "" },
    shiftColorsMap
  );
  if (fromSnap && fromSnap !== "transparent") return fromSnap;
  return (
    shiftColorsMap[shift.shift_id] ??
    (shift.default_shift ? shiftColorsMap[shift.default_shift] : undefined) ??
    shift.color ??
    "#ccc"
  );
}

type ViewTabShiftChip = {
  rowKey: string;
  displayShiftId: string;
  displayName: string;
  color: string;
  count: number;
};

function buildViewTabShiftChips({
  me,
  codeCounts,
  shiftColorsMap,
  availableShifts,
  orderedMainTemplateShifts,
}: {
  me: RosterNurse | undefined;
  codeCounts: Record<string, number>;
  shiftColorsMap: Record<string, string>;
  availableShifts: type.availableShifts[];
  orderedMainTemplateShifts: type.availableShifts[];
}): ViewTabShiftChip[] {
  if (!me) return [];

  const coveredCodeKeys = (): Set<string> => {
    const s = new Set<string>();
    for (const sh of orderedMainTemplateShifts) {
      s.add(sh.shift_id);
      if (sh.default_shift) s.add(sh.default_shift);
    }
    return s;
  };

  const mainRows: ViewTabShiftChip[] = orderedMainTemplateShifts.map(
    (shift) => ({
      rowKey: `main-${shift.id}`,
      /** 표시·집계는 default_shift 순, 칩에 찍는 코드는 shift_id */
      displayShiftId: shift.shift_id,
      displayName: shift.name ?? "",
      color: chipColorForCatalogShift(shift, me, shiftColorsMap),
      count: countForShiftRow(shift, codeCounts),
    })
  );

  const covered = coveredCodeKeys();
  const extraCodes = orderCodesBySnapshotCatalog(
    Object.keys(codeCounts).filter((k) => !covered.has(k)),
    availableShifts
  );

  const extraRows: ViewTabShiftChip[] = extraCodes.map((code) => {
    const cat = (availableShifts ?? []).find(
      (s) => s.shift_id === code || s.default_shift === code
    );
    const firstCell = me.schedule?.find(
      (cell) => normalizeScheduleCellCode(cell) === code
    );
    const color = getSnapshotCellDisplayColor(
      firstCell ?? { code, color: cat?.color ?? "" },
      shiftColorsMap
    );
    return {
      rowKey: `extra-${code}`,
      displayShiftId: code,
      displayName: cat?.name ?? code,
      color: color === "transparent" ? "#ccc" : color,
      count: codeCounts[code] ?? 0,
    };
  });

  if (orderedMainTemplateShifts.length > 0) {
    return [...mainRows, ...extraRows];
  }

  return orderCodesBySnapshotCatalog(
    Object.keys(codeCounts),
    availableShifts
  ).map((code) => {
    const cat = (availableShifts ?? []).find(
      (s) => s.shift_id === code || s.default_shift === code
    );
    const firstCell = me.schedule?.find(
      (cell) => normalizeScheduleCellCode(cell) === code
    );
    const color = getSnapshotCellDisplayColor(
      firstCell ?? { code, color: cat?.color ?? "" },
      shiftColorsMap
    );
    return {
      rowKey: `fb-${code}`,
      displayShiftId: code,
      displayName: cat?.name ?? code,
      color: color === "transparent" ? "#ccc" : color,
      count: codeCounts[code] ?? 0,
    };
  });
}

const CAPTURE_IMAGE_PLACEHOLDER =
  "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==";

function scheduleEntryGroupId(cell: unknown): string | null {
  if (
    cell == null ||
    typeof cell !== "object" ||
    !("group_id" in cell) ||
    typeof (cell as { group_id: unknown }).group_id !== "string"
  ) {
    return null;
  }
  const groupId = (cell as { group_id: string }).group_id.trim();
  return groupId === "" ? null : groupId;
}

function RosterViewMyTabContent({
  issuedList,
  holidayDates,
  currentScheduleDate,
  setCurrentScheduleDate,
  loginUser,
  scheduleId,
  /** 상위 스냅샷 기준(폴백). 표시는 GET /roster/issued_roster/me 의 `issued_at` 우선 */
  issuedDate,
  /** 발행 스냅샷 시프트 목록 — 근무코드 칩(위·아래) 정렬: 띠 → sequence → id */
  availableShifts,
}: {
  issuedList: { year: number; month: number; schedule_id: string }[];
  holidayDates: string[];
  currentScheduleDate: { year: number; month: number; schedule_id: string };
  setCurrentScheduleDate: (value: {
    year: number;
    month: number;
    schedule_id: string;
  }) => void;
  loginUser: type.LoginUser | null;
  scheduleId: string;
  issuedDate?: Date;
  availableShifts: type.availableShifts[];
}) {
  const captureRef = useRef<HTMLDivElement>(null);
  const offscreenRef = useRef<HTMLDivElement>(null);
  const offscreenCaptureCardRef = useRef<HTMLDivElement>(null);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [showCapturePreview, setShowCapturePreview] = useState(false);
  const [selectedWardGroupId, setSelectedWardGroupId] = useState<string | null>(
    null
  );
  /** 복사 성공 시 버튼 라벨만 전환 (토스트 대신 인라인 피드백) */
  const [shareCopySucceeded, setShareCopySucceeded] = useState(false);
  const shareCopyResetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const clearShareCopyFeedback = useCallback(() => {
    if (shareCopyResetTimerRef.current != null) {
      clearTimeout(shareCopyResetTimerRef.current);
      shareCopyResetTimerRef.current = null;
    }
    setShareCopySucceeded(false);
  }, []);

  useEffect(() => {
    if (!isShareModalOpen) clearShareCopyFeedback();
  }, [isShareModalOpen, clearShareCopyFeedback]);

  useEffect(() => {
    setShareCopySucceeded(false);
    if (shareCopyResetTimerRef.current != null) {
      clearTimeout(shareCopyResetTimerRef.current);
      shareCopyResetTimerRef.current = null;
    }
  }, [shareUrl]);

  useEffect(
    () => () => {
      if (shareCopyResetTimerRef.current != null) {
        clearTimeout(shareCopyResetTimerRef.current);
      }
    },
    []
  );
  const loginUserWithProfile = loginUser as type.LoginUser & {
    office_name?: string;
    mb_part_name?: string;
    official_title_name?: string;
  };

  const { data: rosterConfig } = useRosterConfig();
  const isUseMid = rosterConfig?.use_mid;

  /** 메인 템플릿만, `sequence` 오름차순 — 카탈로그(스냅샷 shifts) 값 그대로 */
  const orderedMainTemplateShifts = useMemo(() => {
    const raw = availableShifts ?? [];
    const filtered =
      isUseMid === false ? raw.filter((s) => s.default_shift !== "M") : raw;
    return getDefaultShiftsSortedBySequence(filtered);
  }, [availableShifts, isUseMid]);

  const {
    data: myIssued,
    isPending: myIssuedPending,
    isFetching: myIssuedFetching,
    isError: myIssuedError,
    error: myIssuedErrorObj,
  } = useMyIssuedRoster({
    year: currentScheduleDate.year,
    month: currentScheduleDate.month,
  });

  const myIssuedTransfers = useMemo((): MyIssuedRosterTransfer[] => {
    const list = myIssued?.transfers;
    if (Array.isArray(list) && list.length > 0) {
      return [...list];
    }
    const single = myIssued?.transfer;
    if (single) return [single];
    return [];
  }, [myIssued]);

  const myInboundEntries = useMemo(
    (): IssuedSnapshotAssignmentEntry[] =>
      myIssuedTransfers.map((t) => ({
        reason: t.reason,
        target_group_id: t.target_group_id,
        target_group_name: t.target_group_name,
        start_date: t.start_date,
        end_date: t.end_date,
      })),
    [myIssuedTransfers]
  );

  const currentWardFromIssued = useMemo(
    () => ({
      name: (myIssued?.source_group_name ?? "").trim(),
      groupId: (myIssued?.source_group_id ?? "").trim(),
    }),
    [myIssued?.source_group_name, myIssued?.source_group_id]
  );

  const sourceWardGroupId =
    currentWardFromIssued.groupId ||
    (loginUserWithProfile.group_id ?? "").trim();

  const myDayGroupIds = useMemo(
    (): readonly (string | null)[] =>
      withTransferDayGroupIds(
        (myIssued?.schedule ?? []).map((cell) => scheduleEntryGroupId(cell)),
        myIssuedTransfers
      ),
    [myIssued?.schedule, myIssuedTransfers]
  );

  const wardOptionsForMyTab = useMemo(() => {
    const homeId =
      currentWardFromIssued.groupId ||
      (loginUserWithProfile.group_id ?? "").trim();
    const homeName =
      currentWardFromIssued.name ||
      (loginUserWithProfile.mb_part_name ?? "").trim();

    const groupsFromIssued = Array.isArray(myIssued?.groups)
      ? myIssued.groups
      : [];
    if (groupsFromIssued.length > 0) {
      const mapped = groupsFromIssued
        .map((g) => {
          const rawId = (g.group_id ?? "").trim();
          const label = (g.group_name ?? "").trim();
          if (rawId === "" || label === "") return null;
          return { rawGroupId: rawId, value: rawId, label };
        })
        .filter(
          (v): v is { rawGroupId: string; value: string; label: string } =>
            v != null
        );

      const dedup = new Map<
        string,
        { rawGroupId: string; value: string; label: string }
      >();
      mapped.forEach((o) => dedup.set(o.rawGroupId, o));

      if (homeId !== "") {
        const home = dedup.get(homeId);
        const homeOption = {
          rawGroupId: homeId,
          value: homeId,
          label: homeName || home?.label || homeId,
        };
        dedup.delete(homeId);
        return [homeOption, ...dedup.values()];
      }
      return [...dedup.values()];
    }

    return mergeWardOptionsWithUserHome(
      myInboundEntries.map((e) => ({
        rawGroupId: (e.target_group_id ?? "").trim(),
        value: (e.target_group_id ?? "").trim(),
        label: (e.target_group_name ?? "").trim() || "미지정",
      })),
      loginUserWithProfile as unknown as UserData
    );
  }, [
    myInboundEntries,
    currentWardFromIssued,
    loginUserWithProfile,
    myIssued?.groups,
  ]);

  const wardColorByGroupId = useMemo(() => {
    const palette = assignIndexPalette(wardOptionsForMyTab);
    const homeId =
      currentWardFromIssued.groupId ||
      (loginUserWithProfile.group_id ?? "").trim();
    if (homeId !== "") {
      // 현재 병동은 항상 기본 대표색으로 고정
      palette.set(homeId, "#111827");
    }
    return palette;
  }, [
    wardOptionsForMyTab,
    currentWardFromIssued.groupId,
    loginUserWithProfile.group_id,
  ]);

  const getWardIndicatorLabel = useCallback(
    (rawGroupId: string, label: string): string =>
      rawGroupId === sourceWardGroupId ? "내 병동" : label,
    [sourceWardGroupId]
  );

  useEffect(() => {
    if (selectedWardGroupId == null) return;
    const selectedWardExists = wardOptionsForMyTab.some(
      (option) => option.rawGroupId === selectedWardGroupId
    );
    if (!selectedWardExists) setSelectedWardGroupId(null);
  }, [selectedWardGroupId, wardOptionsForMyTab]);

  const rosterSheetForView = useMemo((): Roster | null => {
    if (!myIssued) return null;
    return myIssuedRosterResponseToRoster(myIssued, {
      year: currentScheduleDate.year,
      month: currentScheduleDate.month,
    });
  }, [myIssued, currentScheduleDate.year, currentScheduleDate.month]);

  const snapshotLoading = myIssuedPending || myIssuedFetching;

  // 단일 간호사 정보 추출 (합성 Roster에는 본인 1명만 존재)
  const me = useMemo(() => {
    const nurses = rosterSheetForView?.nurses ?? [];
    return nurses.find(
      (n) => String(n.nurse_id) === String(loginUser?.nurse_id)
    );
  }, [rosterSheetForView, loginUser?.nurse_id]);

  const shiftColorsMap = useMemo(
    () => rosterSheetForView?.shift_colors ?? {},
    [rosterSheetForView]
  );

  const issuedAtDisplay = useMemo(() => {
    if (myIssued?.issued_at) {
      const d = new Date(myIssued.issued_at);
      if (!Number.isNaN(d.getTime())) return d;
    }
    if (issuedDate != null && !Number.isNaN(issuedDate.getTime()))
      return issuedDate;
    return undefined;
  }, [myIssued?.issued_at, issuedDate]);

  const allCodeCounts = useMemo(() => {
    const acc: Record<string, number> = {};
    if (!me?.schedule) return acc;
    for (const cell of me.schedule) {
      const c = normalizeScheduleCellCode(cell).trim();
      if (!c || c === "-") continue;
      acc[c] = (acc[c] ?? 0) + 1;
    }
    return acc;
  }, [me]);

  const filteredCodeCounts = useMemo(() => {
    if (selectedWardGroupId == null) return allCodeCounts;

    const acc: Record<string, number> = {};
    if (!me?.schedule) return acc;
    for (const [index, cell] of me.schedule.entries()) {
      if (myDayGroupIds[index] !== selectedWardGroupId) {
        continue;
      }
      const c = normalizeScheduleCellCode(cell).trim();
      if (!c || c === "-") continue;
      acc[c] = (acc[c] ?? 0) + 1;
    }
    return acc;
  }, [allCodeCounts, me, myDayGroupIds, selectedWardGroupId]);

  /** 상단 칩·하단 범례: 메인 default_shift(D/E/N/…)는 카탈로그 기준 항상 표시, 하단 텍스트는 `name` */
  const visibleShifts = useMemo((): ViewTabShiftChip[] => {
    return buildViewTabShiftChips({
      me,
      codeCounts: filteredCodeCounts,
      shiftColorsMap,
      availableShifts,
      orderedMainTemplateShifts,
    });
  }, [
    me,
    filteredCodeCounts,
    shiftColorsMap,
    availableShifts,
    orderedMainTemplateShifts,
  ]);

  const captureVisibleShifts = useMemo((): ViewTabShiftChip[] => {
    return buildViewTabShiftChips({
      me,
      codeCounts: allCodeCounts,
      shiftColorsMap,
      availableShifts,
      orderedMainTemplateShifts,
    });
  }, [
    me,
    allCodeCounts,
    shiftColorsMap,
    availableShifts,
    orderedMainTemplateShifts,
  ]);

  // 근무표 공유하기 - 이미지 캡처 → API 호출 → 모달에 share_url 표시
  const shareMutation = useMutation({
    mutationFn: async () => {
      if (!scheduleId) {
        throw new Error("공유할 근무표가 없습니다.");
      }

      // 1. 이미지 캡처 (다운로드와 동일한 로직)
      type FontReadyDocument = Document & { fonts?: { ready: Promise<void> } };
      const docWithFonts = document as FontReadyDocument;
      if (docWithFonts.fonts?.ready) {
        await docWithFonts.fonts.ready;
      }

      const node =
        offscreenCaptureCardRef.current ??
        document.querySelector<HTMLElement>('[data-roster-share-capture="true"]') ??
        offscreenRef.current ??
        captureRef.current;
      if (!node) throw new Error("캡처할 영역을 찾을 수 없습니다.");

      // 캡처 기준 노드(오프스크린 카드)를 그대로 사용
      const targetNode = node;
      const rectNow = targetNode.getBoundingClientRect();
      const width = Math.ceil(targetNode.scrollWidth || rectNow.width);
      const height = Math.ceil(targetNode.scrollHeight || rectNow.height);
      if (width <= 0 || height <= 0) {
        throw new Error("캡처할 근무표 영역이 아직 준비되지 않았습니다.");
      }

      const imageDataUrl = await toPng(targetNode, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: "#ffffff",
        imagePlaceholder: CAPTURE_IMAGE_PLACEHOLDER,
        width,
        height,
        style: {
          opacity: "1",
          transform: "none",
          position: "absolute",
          top: "0",
          left: "0",
          zIndex: "9999",
          pointerEvents: "none",
        },
      });

      // 2. API 호출 - POST /roster/shares/schedules/{schedule_id}/capture
      const response = await httpClient.post<{
        token: string;
        share_url: string;
        image_url: string;
        expires_at: string;
        schedule_id: string;
        group_id?: string;
        office_id?: string;
      }>(`/roster/shares/schedules/${scheduleId}/capture`, {
        image_data_url: imageDataUrl,
        title: `${currentScheduleDate.year}년 ${currentScheduleDate.month}월 근무표`,
        description: "공유된 근무표입니다.",
        expires_in_days: 3,
      });

      return response;
    },
    onSuccess: (data) => {
      setShareUrl(data.share_url);
      setIsShareModalOpen(true);
      toast.success("근무표 공유 링크가 생성되었습니다.");
    },
    onError: (error: Error) => {
      console.error("근무표 공유 실패:", error);
      setIsShareModalOpen(false);
      toast.error(error?.message ?? "근무표 공유에 실패했습니다.");
    },
  });

  // 공유 링크 복사 (성공 피드백은 버튼 인라인 — 토스트 없음)
  const handleCopyShareUrl = async () => {
    if (!shareUrl) return;

    const markCopied = () => {
      if (shareCopyResetTimerRef.current != null) {
        clearTimeout(shareCopyResetTimerRef.current);
      }
      setShareCopySucceeded(true);
      shareCopyResetTimerRef.current = setTimeout(() => {
        setShareCopySucceeded(false);
        shareCopyResetTimerRef.current = null;
      }, 2200);
    };

    try {
      await navigator.clipboard.writeText(shareUrl);
      markCopied();
    } catch {
      const textArea = document.createElement("textarea");
      textArea.value = shareUrl;
      textArea.style.position = "fixed";
      textArea.style.opacity = "0";
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand("copy");
        markCopied();
      } catch (fallbackError) {
        toast.error("링크 복사에 실패했습니다.");
        console.error("Clipboard copy failed:", fallbackError);
      }
      document.body.removeChild(textArea);
    }
  };

  const downloadMutation = useMutation({
    mutationFn: async () => {
      // 우선 폰트가 모두 로드되도록 대기
      type FontReadyDocument = Document & { fonts?: { ready: Promise<void> } };
      const docWithFonts = document as FontReadyDocument;
      if (docWithFonts.fonts?.ready) {
        await docWithFonts.fonts.ready;
      }

      // 오프스크린 카드 노드를 우선 사용, 없으면 기존 ref 사용
      const node =
        offscreenCaptureCardRef.current ??
        document.querySelector<HTMLElement>('[data-roster-share-capture="true"]') ??
        offscreenRef.current ??
        captureRef.current;
      if (!node) throw new Error("캡처할 영역을 찾을 수 없습니다.");

      // 캡처 기준 노드(오프스크린 카드)를 그대로 사용
      const targetNode = node;
      const rectNow = targetNode.getBoundingClientRect();
      const width = Math.ceil(targetNode.scrollWidth || rectNow.width);
      const height = Math.ceil(targetNode.scrollHeight || rectNow.height);
      if (width <= 0 || height <= 0) {
        throw new Error("캡처할 근무표 영역이 아직 준비되지 않았습니다.");
      }
      const ym = `${currentScheduleDate.year}-${String(currentScheduleDate.month).padStart(2, "0")}`;
      const userName = loginUser?.name ?? "user";

      const dataUrl = await toPng(targetNode, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: "#ffffff",
        imagePlaceholder: CAPTURE_IMAGE_PLACEHOLDER,
        width,
        height,
        // 클론(캡처본) 스타일 재정의: 투명도를 1로 설정하여 캡처 이미지에는 보이도록 함
        style: {
          opacity: "1",
          transform: "none",
          position: "absolute",
          top: "0",
          left: "0",
          zIndex: "9999", // 캡처 시 맨 위에 오도록
          pointerEvents: "none",
        },
      });

      return { dataUrl, fileName: `근무표_${ym}_${userName}.png` };
    },
    onSuccess: ({ dataUrl, fileName }) => {
      const link = document.createElement("a");
      link.download = fileName;
      link.href = dataUrl;
      link.click();
    },
  });

  const captureBlocked = snapshotLoading || rosterSheetForView == null;

  return (
    <div className="pb-15 flex flex-col justify-center " ref={captureRef}>
      {/* Figma 근무표 보기: 월 선택 | 마감일시(중앙) | 공유·다운로드 */}

      {snapshotLoading ? (
        <div
          className="pointer-events-none py-10  inset-0 z-40 flex items-center justify-center bg-white/55"
          aria-busy
          aria-label="근무표 불러오는 중"
        >
          <LoadingSpiner msg="근무표를 불러오는 중입니다" />
        </div>
      ) : myIssuedError ? (
        <div className="px-5 py-16 text-center text-red-600 text-sm">
          {myIssuedErrorObj instanceof Error
            ? myIssuedErrorObj.message
            : "내 근무표를 불러오지 못했습니다."}
        </div>
      ) : myIssued === null ? (
        <div className="px-5  py-16 text-center text-zinc-500">
          발행된 근무표가 없습니다.
        </div>
      ) : myIssued ? (
        <div className="mx-auto w-full flex flex-col justify-center items-center gap-4 px-5 pb-10 ">
          <div className="w-full max-w-[540px]">
            <Card>
              <CardContent className="flex justify-between items-center px-8">
                <div className="flex flex-col items-start">
                  <RosterListButton
                    issuedList={issuedList}
                    currentScheduleDate={currentScheduleDate}
                    setCurrentScheduleDate={setCurrentScheduleDate}
                  />
                  <div className="mt-2">
                    <IssuedDate issuedAtDisplay={issuedAtDisplay} />
                  </div>
                </div>
                <div className="flex justify-end gap-2 justify-self-end">
                  {import.meta.env.VITE_ENV === "LOCAL" && (
                    <BtnIcon
                      size={"icon-lg"}
                      onClick={() => setShowCapturePreview((prev) => !prev)}
                      tooltip={
                        showCapturePreview
                          ? "캡처 미리보기 숨기기"
                          : "캡처 미리보기"
                      }
                      icon={
                        showCapturePreview ? (
                          <EyeOff className="size-4" />
                        ) : (
                          <Eye className="size-4" />
                        )
                      }
                    />
                  )}
                  <BtnIcon
                    size={"icon-lg"}
                    onClick={() => {
                      setShareUrl(null);
                      setIsShareModalOpen(true);
                      shareMutation.mutate();
                    }}
                    disabled={shareMutation.isPending || captureBlocked}
                    tooltip="근무표 공유 주소를 생성합니다."
                    icon={<Share2 className="size-4" />}
                  />
                  <BtnIcon
                    size={"icon-lg"}
                    onClick={() => downloadMutation.mutate()}
                    disabled={downloadMutation.isPending || captureBlocked}
                    tooltip="근무표 다운로드"
                    icon={<Download className="size-4" />}
                  />
                </div>
              </CardContent>
            </Card>
            <div className="flex items-center gap-3 px-5 py-3  sm:gap-x-4"></div>

            <Card className="">
              <div className=" rounded-[10px] bg-white  px-8 py-0 ">
                <div className="mb-5">
                  <div className="flex items-center justify-between gap-3">
                    <WardFilterLabel />
                    <div className="text-xs font-medium text-muted-foreground">
                      선택한 병동의 근무만 강조합니다.
                    </div>
                  </div>
                  <div
                    className="mt-2 flex min-h-[44px] flex-wrap items-center gap-2 rounded-lg border border-border bg-muted/30 px-2 py-2"
                    aria-label="병동 필터"
                  >
                    {wardOptionsForMyTab.length > 0 ? (
                      wardOptionsForMyTab.map((o) => {
                        const isSelected =
                          selectedWardGroupId === o.rawGroupId;
                        const isDimmed =
                          selectedWardGroupId !== null && !isSelected;
                        return (
                          <button
                            key={`${o.rawGroupId}-${o.value}`}
                            type="button"
                            aria-pressed={isSelected}
                            onClick={() =>
                              setSelectedWardGroupId((prev) =>
                                prev === o.rawGroupId ? null : o.rawGroupId
                              )
                            }
                            className={cn(
                              "rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sig/30"
                            )}
                          >
                            <WardFilterChip
                              label={getWardIndicatorLabel(
                                o.rawGroupId,
                                o.label
                              )}
                              selected={isSelected}
                              dimmed={isDimmed}
                            />
                          </button>
                        );
                      })
                    ) : (
                      <WardFilterChip
                        label="내 병동"
                        selected={false}
                        dimmed={false}
                      />
                    )}
                  </div>
                </div>

                <div className="flex w-full justify-center ">
                  {rosterSheetForView && (
                    <ViewRosterCalendarC
                      currentScheduleDate={currentScheduleDate}
                      holidayDates={holidayDates}
                      loginUser={loginUser}
                      rosterSheet={rosterSheetForView}
                      myAssignments={myInboundEntries}
                      myTransfers={myIssuedTransfers}
                      myDayGroupIds={myDayGroupIds}
                      sourceWardGroupId={sourceWardGroupId}
                      selectedWardGroupId={selectedWardGroupId}
                      wardColorByGroupId={wardColorByGroupId}
                    />
                  )}
                </div>

                {/* 월별 근무코드별 일수 칩 — 캘린더 아래 */}
                <div className="flex flex-col gap-2 mt-8 ">
                  <div className="text-sm font-semibold font-pretendard text-secondary-foreground">
                    근무 코드
                  </div>
                  <div className="grid grid-cols-6 gap-2">
                    {visibleShifts.map((shift) => (
                      <div
                        key={shift.rowKey}
                        className={cn(
                          "flex flex-col transition-opacity",
                          selectedWardGroupId != null &&
                            shift.count === 0 &&
                            "opacity-35"
                        )}
                      >
                        <div
                          className="grid grid-cols-2 items-center border  rounded-sm overflow-hidden"
                        >
                          <div
                            className="aspect-square text-white font-semibold font-pretendard flex items-center justify-center"
                            style={{
                              backgroundColor: shift.color,
                              color: getReadableShiftCodeTextColor(shift.color),
                            }}
                          >
                            {shift.displayShiftId}
                          </div>
                          <div className="size-8 text-sm font-semibold font-pretendard flex items-center justify-center">
                            {shift.count}일
                          </div>
                        </div>
                        <div className="mt-1 w-full min-w-0 text-center text-xs font-pretendard text-muted-foreground/80">
                          <span className="block truncate">
                            {shift.displayName}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      ) : null}

      {/* 오프스크린 캡처용 동일 DOM (UI 변경 없이 가상 화면 렌더링) */}
      <div
        ref={offscreenRef}
        aria-hidden="true"
        className={
          showCapturePreview
            ? "fixed right-4 top-20 z-50"
            : "fixed -left-[10000px] top-0 -z-10 bg-white"
        }
        style={{
          position: "fixed",
          opacity: 1,
          pointerEvents: showCapturePreview ? "auto" : "none",
          transform: "none",
        }}
      >
        {/* 스냅샷 전용 레이아웃 */}
        <div
          ref={offscreenCaptureCardRef}
          data-roster-share-capture="true"
          className="bg-custom-gradient w-[550px] rounded-[10px] shadow-sm overflow-hidden"
        >
          {/* 배너 */}
          <div className="w-full pt-10 pb-6  flex flex-col items-center justify-center  text-center">
            <div className="text-4xl font-semibold text-secondary-foreground">
              {`${currentScheduleDate.year}년 ${String(currentScheduleDate.month).padStart(2, "0")}월 근무표`}
            </div>
            <div className="text-lg  mt-2">
              {[
                loginUserWithProfile.office_name,
                loginUserWithProfile.mb_part_name,
                loginUser?.name,
              ]
                .filter(Boolean)
                .join(" ")}
            </div>
          </div>

          <div className="px-4 pb-6 pt-4 bg-background">
            {/* 본문 캘린더 */}
            <div className="w-full flex justify-center ">
              {rosterSheetForView && (
                <ViewRosterCalendarC
                  currentScheduleDate={currentScheduleDate}
                  holidayDates={holidayDates}
                  loginUser={loginUser}
                  rosterSheet={rosterSheetForView}
                  myAssignments={myInboundEntries}
                  myTransfers={myIssuedTransfers}
                  myDayGroupIds={myDayGroupIds}
                  sourceWardGroupId={sourceWardGroupId}
                  selectedWardGroupId={null}
                  wardColorByGroupId={wardColorByGroupId}
                />
              )}
            </div>

            <div className="flex flex-col gap-2 mt-8 ">
              <div className="text-sm font-semibold font-pretendard text-secondary-foreground">
                근무 코드
              </div>
              <div className="grid grid-cols-6 gap-2">
                {captureVisibleShifts.map((shift) => (
                  <div key={shift.rowKey} className="flex flex-col">
                    <div
                      className="grid grid-cols-2 items-center border  rounded-sm overflow-hidden"
                    >
                      <div
                        className="aspect-square text-white font-semibold font-pretendard flex items-center justify-center"
                        style={{
                          backgroundColor: shift.color,
                          color: getReadableShiftCodeTextColor(shift.color),
                        }}
                      >
                        {shift.displayShiftId}
                      </div>
                      <div className="size-8 text-sm font-semibold font-pretendard flex items-center justify-center">
                        {shift.count}일
                      </div>
                    </div>
                    <div className="mt-1 w-full min-w-0 text-center text-xs font-pretendard text-muted-foreground/80">
                      <span className="block truncate">
                        {shift.displayName}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* 하단 브랜드: 캡쳐 안정성을 위해 img 로드를 사용하지 않는다. */}
          <div className="flex justify-between p-5 text-sm font-semibold tracking-[0.08em] text-white/90">
            <span>meditong</span>
            <span>an-maum</span>
          </div>
        </div>
      </div>

      {/* 근무표 공유 모달 */}
      <CustomModal.Confirm
        isOpen={isShareModalOpen}
        onClose={() => {
          clearShareCopyFeedback();
          setIsShareModalOpen(false);
          setShareUrl(null);
        }}
        isPending={shareMutation.isPending}
        pendingMsg="공유 링크를 생성 중입니다..."
        header
        headerTitle="근무표 공유하기"
        showActions={false}
      >
        {shareUrl ? (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                공유 링크
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  readOnly
                  value={shareUrl}
                  className="flex-1 px-3 py-2 border min-w-[250px] border-gray-300 rounded-md bg-gray-50 text-sm"
                />
                <button
                  type="button"
                  onClick={handleCopyShareUrl}
                  aria-live="polite"
                  className={cn(
                    "flex min-w-[104px] shrink-0 items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors",
                    shareCopySucceeded
                      ? "bg-emerald-600 text-white"
                      : "bg-[#0E59C6] text-white hover:bg-[#0052CC]"
                  )}
                >
                  {shareCopySucceeded ? (
                    <>
                      <Check className="size-4 shrink-0" aria-hidden />
                      복사 완료
                    </>
                  ) : (
                    <>
                      <Copy className="size-4 shrink-0" aria-hidden />
                      복사
                    </>
                  )}
                </button>
              </div>
            </div>
            <p className="text-xs text-gray-500 mb-4">
              링크는 3일 후 만료됩니다.
            </p>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => {
                  clearShareCopyFeedback();
                  setIsShareModalOpen(false);
                  setShareUrl(null);
                }}
                className="px-6 py-2 w-full bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
              >
                닫기
              </button>
            </div>
          </>
        ) : (
          <div className="text-center py-4">
            <p className="text-sm text-gray-600">
              공유 링크를 생성하는 중입니다...
            </p>
          </div>
        )}
      </CustomModal.Confirm>
    </div>
  );
}

export default RosterViewMyTabContent;
